// Mantenimiento M1 (M50): Inventario de activos, oculto para clientes (EMPRESA TEST y superadmin). Primer uso del
// tablero común: tarjetas de resumen que filtran, grupos por estado operativo, filtros combinables y guardados,
// búsqueda, edición directa (nombre, categoría, área, responsable y criticidad), acciones masivas autorizadas (nunca
// estado ni aptitud) y la ficha en un panel lateral. Roles: viewer mira; admin y manager editan; el código lo cambia
// el admin (como en la base). Un enlace de QR (?activo=<id>) abre la ficha después de iniciar sesión.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCompany } from '../../CompanyContext';
import { useImpersonation } from '../../ImpersonationContext';
import { useSedes } from '../../lib/useSedes';
import Tablero, { Etiqueta, PanelLateral, type AccionMasivaTablero } from '../../components/tablero/Tablero';
import { aplicarFiltros, type Agrupacion, type ColumnaTablero, type EstadoFiltros, type FiltroDef } from '../../components/tablero/tablero-logica';
import { fechaEc } from '../../lib/inspecciones/insp-logica';
import {
  APTITUDES, CATEGORIAS, COLOR_APTITUD, COLOR_CRITICIDAD, COLOR_ESTADO, CRITICIDADES, ESTADOS, ETIQUETA_APTITUD, ETIQUETA_CRITICIDAD,
  ETIQUETA_ESTADO, LISTA_CATEGORIAS, PLAZOS_NORMA, etiquetaCategoria, esEmergencia, type Categoria, type Criticidad, type FilaActivo,
  type PlazosExtintor as Plazos,
} from '../../lib/mantenimiento/mant-tipos';
import {
  ETIQUETA_TARJETA, ETIQUETA_VENCIMIENTO, FILTRO_TARJETA, proximoVencimiento, resumen, type Tarjeta,
} from '../../lib/mantenimiento/mant-logica';
import {
  cambioMasivo, cargarActivo, cargarActivos, cargarOpciones, cargarPlazosExtintor, actualizarActivo, ultimasInspecciones, type Opcion,
} from '../../lib/mantenimiento/mant-datos';
import { descargarLibro, filasExport, libroExport } from '../../lib/mantenimiento/mant-importar';
import { CLAVE_QR_PENDIENTE } from '../../lib/mantenimiento/mant-qr';
import FichaActivo from './FichaActivo';
import ImportarActivos from './ImportarActivos';
import PlazosExtintor from './PlazosExtintor';
import '../pe/PlanEmergenciasV2.css';
import '../inspecciones/InspeccionesV2.css';
import './InventarioActivos.css';
import { hoyEc } from '../../lib/fechas';

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));
type Panel = { tipo: 'ficha'; id: string } | { tipo: 'nuevo' } | { tipo: 'importar' } | { tipo: 'plazos' } | null;
const TARJETAS: Tarjeta[] = ['total', 'operativos', 'con_observaciones', 'fuera_servicio', 'criticos'];
const COLOR_TARJETA: Partial<Record<Tarjeta, string>> = {
  operativos: COLOR_ESTADO.operativo, con_observaciones: COLOR_ESTADO.con_observaciones, fuera_servicio: COLOR_ESTADO.fuera_servicio, criticos: COLOR_CRITICIDAD.critica,
};
const colorVence = { vencido: '#ef4444', por_vencer: '#eab308', vigente: '#22c55e' } as const;
const igualFiltro = (a: EstadoFiltros, b: Record<string, string[]>) => {
  const ka = Object.keys(a).filter(k => a[k].length), kb = Object.keys(b);
  return ka.length === kb.length && kb.every(k => JSON.stringify([...(a[k] ?? [])].sort()) === JSON.stringify([...b[k]].sort()));
};

export default function InventarioActivos({ hoy: hoyProp }: { hoy?: Date } = {}) {
  const hoy = useMemo(() => hoyProp ?? new Date(), [hoyProp]);
  const { company, profile } = useCompany();
  const { impersonated } = useImpersonation();
  const empresa = impersonated ?? company;
  const companyId = empresa?.id ?? '';
  const puedeEditar = profile?.role === 'admin' || profile?.role === 'manager' || !!impersonated;
  const esAdmin = profile?.role === 'admin' || !!impersonated;
  const usuarioId = profile?.id ?? null;
  const { sedes, varias } = useSedes(companyId);

  const [activos, setActivos] = useState<FilaActivo[]>([]);
  const [opciones, setOpciones] = useState<{ trabajadores: Opcion[]; usuarios: Opcion[]; epp: Opcion[]; sectores: Opcion[] }>({ trabajadores: [], usuarios: [], epp: [], sectores: [] });
  const [ultimas, setUltimas] = useState<Map<string, string>>(new Map());
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [panel, setPanel] = useState<Panel>(null);
  const [filtros, setFiltros] = useState<EstadoFiltros>({});
  const [busqueda, setBusqueda] = useState('');
  const [grupo, setGrupo] = useState('estado');
  const [verArchivados, setVerArchivados] = useState(false);
  // Plazos de los extintores (NTE INEN 739:2016 y AM 1257): los de la empresa o, sin fila, los de la norma.
  const [plazos, setPlazos] = useState<{ plazos: Plazos; propios: boolean }>({ plazos: PLAZOS_NORMA, propios: false });
  useEffect(() => { if (companyId) cargarPlazosExtintor(companyId).then(setPlazos); }, [companyId]);

  const recargar = useCallback(async () => {
    if (!companyId) return;
    try {
      const [a, u] = await Promise.all([cargarActivos(companyId), ultimasInspecciones(companyId)]);
      setActivos(a); setUltimas(u); setError('');
    } catch (e) { setError(msg(e)); }
  }, [companyId]);

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    (async () => {
      await recargar();
      const o = await cargarOpciones(companyId).catch(() => null);
      if (vivo) { if (o) setOpciones(o); setCargando(false); }
    })();
    return () => { vivo = false; };
  }, [recargar, companyId]);

  // Enlace de QR: el id quedó guardado al entrar con ?activo=…; se abre solo si la RLS deja ver el activo.
  useEffect(() => {
    if (!companyId) return;
    let id: string | null = null;
    try { id = sessionStorage.getItem(CLAVE_QR_PENDIENTE); sessionStorage.removeItem(CLAVE_QR_PENDIENTE); } catch { id = null; }
    if (!id) return;
    cargarActivo(id).then(a => {
      if (a && a.company_id === companyId) setPanel({ tipo: 'ficha', id: a.id });
      else setAviso('El activo del código QR no existe o no es de tu empresa.');
    });
  }, [companyId]);

  const sedeNombre = useCallback((id: string | null) => sedes.find(s => s.id === id)?.name ?? '', [sedes]);
  const nombreDe = (l: Opcion[], id: string | null) => l.find(x => x.id === id)?.nombre ?? '';
  const responsable = useCallback((a: FilaActivo) => nombreDe(opciones.trabajadores, a.responsible_worker_id) || nombreDe(opciones.usuarios, a.responsible_user_id),
    [opciones]);
  const venceTexto = useCallback((a: FilaActivo) => { const v = proximoVencimiento(a, hoy, plazos.plazos); return v ? `${ETIQUETA_VENCIMIENTO[v.estado]}: ${fechaEc(v.fecha)}` : ''; },
    [hoy, plazos.plazos]);

  const filas = useMemo(() => activos.filter(a => verArchivados ? !!a.archived_at : !a.archived_at), [activos, verArchivados]);
  const codigos = useMemo(() => activos.map(a => a.code), [activos]);
  const res = resumen(activos);

  const opcionesTrabajador = useMemo(() => opciones.trabajadores.map(w => ({ valor: w.id, etiqueta: w.nombre })), [opciones.trabajadores]);
  const columnas = useMemo<ColumnaTablero<FilaActivo>[]>(() => [
    { clave: 'activo', titulo: 'Activo', texto: a => `${a.code} ${a.name}`, ancho: 2 },
    { clave: 'nombre', titulo: 'Nombre', texto: a => a.name, edicion: () => ({ tipo: 'texto', max: 160 }), ancho: 1.6, ocultarEnMovil: true },
    { clave: 'categoria', titulo: 'Categoría', texto: a => etiquetaCategoria(a.category), valorEdicion: a => a.category, ancho: 1.4,
      edicion: () => ({ tipo: 'opcion', opciones: LISTA_CATEGORIAS.map(c => ({ valor: c, etiqueta: CATEGORIAS[c].etiqueta })) }) },
    ...(varias ? [{ clave: 'sede', titulo: 'Sede', texto: (a: FilaActivo) => sedeNombre(a.site_id), ancho: 1, ocultarEnMovil: true }] : []),
    { clave: 'area', titulo: 'Área', texto: a => a.area ?? '', edicion: () => ({ tipo: 'texto', max: 160 }), ancho: 1 },
    { clave: 'responsable', titulo: 'Responsable', texto: responsable, valorEdicion: a => a.responsible_worker_id ?? '', ancho: 1.2,
      edicion: () => ({ tipo: 'opcion', opciones: opcionesTrabajador, vacio: 'Sin asignar' }) },
    { clave: 'criticidad', titulo: 'Criticidad', texto: a => ETIQUETA_CRITICIDAD[a.criticality], valorEdicion: a => a.criticality, ancho: 0.9,
      edicion: () => ({ tipo: 'opcion', opciones: CRITICIDADES.map(c => ({ valor: c, etiqueta: ETIQUETA_CRITICIDAD[c] })) }) },
    { clave: 'estado', titulo: 'Estado', texto: a => ETIQUETA_ESTADO[a.operational_status], ancho: 1.1 },
    { clave: 'aptitud', titulo: 'Aptitud', texto: a => ETIQUETA_APTITUD[a.safety_status], ancho: 0.8 },
    { clave: 'vence', titulo: 'Vencimiento', texto: venceTexto, ancho: 1.1, ocultarEnMovil: true },
    { clave: 'inspeccion', titulo: 'Última inspección', texto: a => (ultimas.get(a.id) ? fechaEc(ultimas.get(a.id)) : ''), ancho: 0.9, ocultarEnMovil: true, buscable: false },
  ], [varias, sedeNombre, responsable, opcionesTrabajador, venceTexto, ultimas]);

  const defsFiltro = useMemo<FiltroDef<FilaActivo>[]>(() => [
    { clave: 'operational_status', titulo: 'Estado operativo', opciones: ESTADOS.map(s => ({ valor: s, etiqueta: ETIQUETA_ESTADO[s] })), valor: a => a.operational_status },
    { clave: 'safety_status', titulo: 'Aptitud de seguridad', opciones: APTITUDES.map(s => ({ valor: s, etiqueta: ETIQUETA_APTITUD[s] })), valor: a => a.safety_status },
    { clave: 'criticality', titulo: 'Criticidad', opciones: CRITICIDADES.map(c => ({ valor: c, etiqueta: ETIQUETA_CRITICIDAD[c] })), valor: a => a.criticality },
    { clave: 'category', titulo: 'Categoría', opciones: LISTA_CATEGORIAS.map(c => ({ valor: c, etiqueta: CATEGORIAS[c].etiqueta })), valor: a => a.category },
    { clave: 'tipo', titulo: 'Tipo', opciones: [{ valor: 'emergencia', etiqueta: 'Equipos de emergencia' }, { valor: 'otros', etiqueta: 'Otros activos' }],
      valor: a => (esEmergencia(a.category) ? 'emergencia' : 'otros') },
    { clave: 'vence', titulo: 'Vencimientos', opciones: [{ valor: 'vencido', etiqueta: 'Vencidos' }, { valor: 'por_vencer', etiqueta: 'Por vencer (30 días)' },
      { valor: 'vigente', etiqueta: 'Vigentes' }, { valor: '', etiqueta: 'Sin vencimientos' }], valor: a => proximoVencimiento(a, hoy, plazos.plazos)?.estado ?? '' },
    { clave: 'responsable', titulo: 'Responsable', opciones: [{ valor: 'con', etiqueta: 'Con responsable' }, { valor: 'sin', etiqueta: 'Sin responsable' }],
      valor: a => (a.responsible_worker_id || a.responsible_user_id ? 'con' : 'sin') },
    ...(varias ? [{ clave: 'site_id', titulo: 'Sede', opciones: [...sedes.map(s => ({ valor: s.id, etiqueta: s.name })), { valor: '', etiqueta: 'Sin sede' }],
      valor: (a: FilaActivo) => a.site_id ?? '' }] : []),
  ], [hoy, varias, sedes, plazos.plazos]);

  const agrupaciones = useMemo<Agrupacion<FilaActivo>[]>(() => [
    { clave: 'estado', titulo: 'Estado operativo', valor: a => a.operational_status, grupos: ESTADOS.map(s => ({ valor: s, etiqueta: ETIQUETA_ESTADO[s], color: COLOR_ESTADO[s] })) },
    { clave: 'aptitud', titulo: 'Aptitud de seguridad', valor: a => a.safety_status, grupos: APTITUDES.map(s => ({ valor: s, etiqueta: ETIQUETA_APTITUD[s], color: COLOR_APTITUD[s] })) },
    { clave: 'criticidad', titulo: 'Criticidad', valor: a => a.criticality,
      grupos: [...CRITICIDADES].reverse().map(c => ({ valor: c, etiqueta: ETIQUETA_CRITICIDAD[c], color: COLOR_CRITICIDAD[c] })) },
    { clave: 'categoria', titulo: 'Categoría', valor: a => a.category, grupos: LISTA_CATEGORIAS.map(c => ({ valor: c, etiqueta: CATEGORIAS[c].etiqueta, color: CATEGORIAS[c].emergencia ? '#f97316' : '#38bdf8' })) },
    ...(varias ? [{ clave: 'sede', titulo: 'Sede', valor: (a: FilaActivo) => a.site_id ?? '', grupos: sedes.map(s => ({ valor: s.id, etiqueta: s.name, color: '#a78bfa' })) }] : []),
  ], [varias, sedes]);

  const visibles = useMemo(() => aplicarFiltros(filas, defsFiltro, filtros, busqueda, columnas), [filas, defsFiltro, filtros, busqueda, columnas]);
  const actualizarLocal = (a: FilaActivo) => setActivos(l => (l.some(x => x.id === a.id) ? l.map(x => (x.id === a.id ? a : x)) : [...l, a].sort((x, y) => x.code.localeCompare(y.code))));

  const editarCelda = async (id: string, clave: string, valor: string) => {
    const cambio = clave === 'nombre' ? { name: valor.trim() } : clave === 'categoria' ? { category: valor as Categoria }
      : clave === 'area' ? { area: valor.trim() || null } : clave === 'responsable' ? { responsible_worker_id: valor || null }
        : clave === 'criticidad' ? { criticality: valor as Criticidad } : null;
    if (!cambio) return;
    if ('name' in cambio && !cambio.name) throw new Error('El nombre no puede quedar vacío.');
    actualizarLocal(await actualizarActivo(id, cambio));
  };

  const exportar = (lista: FilaActivo[]) => descargarLibro(libroExport(filasExport(lista, { sede: sedeNombre, responsable, vencimiento: venceTexto })),
    `Inventario_activos_${hoyEc()}.xlsx`);

  const masivo = async (ids: string[], cambio: Parameters<typeof cambioMasivo>[1], texto: string) => {
    const n = await cambioMasivo(ids, cambio);
    await recargar();
    return `${texto}: ${n} ${n === 1 ? 'activo' : 'activos'}.`;
  };

  const acciones: AccionMasivaTablero[] = puedeEditar && !verArchivados ? [
    { clave: 'responsable', etiqueta: 'Asignar responsable', formulario: (ids, cerrar) => (
      <FormMasivo etiqueta="Trabajador responsable" opciones={[{ valor: '', etiqueta: 'Sin asignar' }, ...opcionesTrabajador]} ids={ids} cerrar={cerrar}
        aplicar={v => masivo(ids, { responsible_worker_id: v || null }, 'Responsable asignado').then(setAviso)} />) },
    { clave: 'ubicacion', etiqueta: 'Cambiar ubicación', formulario: (ids, cerrar) => (
      <FormUbicacion sedes={varias ? sedes.filter(s => s.active).map(s => ({ valor: s.id, etiqueta: s.name })) : []} ids={ids} cerrar={cerrar}
        aplicar={(sede, area) => masivo(ids, { ...(varias ? { site_id: sede || null } : {}), area: area.trim() || null }, 'Ubicación cambiada').then(setAviso)} />) },
    { clave: 'categoria', etiqueta: 'Cambiar categoría', formulario: (ids, cerrar) => (
      <FormMasivo etiqueta="Categoría" opciones={LISTA_CATEGORIAS.map(c => ({ valor: c, etiqueta: CATEGORIAS[c].etiqueta }))} ids={ids} cerrar={cerrar}
        aplicar={v => masivo(ids, { category: v as Categoria }, 'Categoría cambiada').then(setAviso)} />) },
    { clave: 'archivar', etiqueta: 'Archivar', peligro: true,
      confirmar: n => `¿Archivar ${n} ${n === 1 ? 'activo' : 'activos'}? Quedan de solo lectura y fuera del tablero; su historial se conserva y se pueden restaurar uno por uno.`,
      ejecutar: ids => masivo(ids, { archived_at: new Date().toISOString() }, 'Archivados') },
    { clave: 'exportar', etiqueta: 'Exportar a Excel', ejecutar: async ids => { exportar(activos.filter(a => ids.includes(a.id))); return 'Exportación descargada.'; } },
  ] : [{ clave: 'exportar', etiqueta: 'Exportar a Excel', ejecutar: async ids => { exportar(activos.filter(a => ids.includes(a.id))); return 'Exportación descargada.'; } }];

  const celda = (a: FilaActivo, c: ColumnaTablero<FilaActivo>) => {
    switch (c.clave) {
      case 'activo': return <span className="mant-activo"><b>{a.code}</b><span>{a.name}</span>{esEmergencia(a.category) && <small>Equipo de emergencia</small>}</span>;
      case 'estado': return <Etiqueta texto={ETIQUETA_ESTADO[a.operational_status]} color={COLOR_ESTADO[a.operational_status]} />;
      case 'aptitud': return <Etiqueta texto={ETIQUETA_APTITUD[a.safety_status]} color={COLOR_APTITUD[a.safety_status]} />;
      case 'criticidad': return <Etiqueta texto={ETIQUETA_CRITICIDAD[a.criticality]} color={COLOR_CRITICIDAD[a.criticality]} />;
      case 'vence': { const v = proximoVencimiento(a, hoy, plazos.plazos); return v ? <Etiqueta texto={`${ETIQUETA_VENCIMIENTO[v.estado]} ${fechaEc(v.fecha)}`} color={colorVence[v.estado]} /> : '—'; }
      default: return c.texto(a) || '—';
    }
  };

  if (!companyId) return <div className="pe2"><p className="pe2-nota">Elige una empresa.</p></div>;
  const abierto = panel?.tipo === 'ficha' ? activos.find(a => a.id === panel.id) ?? null : null;
  const tarjetas = TARJETAS.map(t => ({
    clave: t, etiqueta: ETIQUETA_TARJETA[t], valor: res[t], color: COLOR_TARJETA[t],
    activa: t !== 'total' ? igualFiltro(filtros, FILTRO_TARJETA[t]) : Object.values(filtros).every(v => !v.length) && !busqueda,
    onClick: () => { setVerArchivados(false); setFiltros(FILTRO_TARJETA[t]); setBusqueda(''); },
  }));
  const vacio = (
    <div className="pe2-card mant-vacio">
      <h3>{verArchivados ? 'No hay activos archivados' : 'Aún no hay activos en el inventario'}</h3>
      {!verArchivados && (
        <>
          <p>Registra tus máquinas, equipos, instalaciones y equipos de emergencia (extintores, lámparas de emergencia, detectores, pulsadores, botiquines y gabinetes) en un solo lugar.</p>
          <ol>
            <li>Registra el primer activo: nombre y categoría bastan; el código se asigna solo.</li>
            <li>O importa tu lista desde Excel con la plantilla.</li>
            <li>Después programa sus inspecciones e imprime su etiqueta con QR.</li>
          </ol>
          {puedeEditar ? (
            <div className="pe2-acciones" style={{ justifyContent: 'flex-start' }}>
              <button type="button" className="pe2-btn primario" onClick={() => setPanel({ tipo: 'nuevo' })}>Registrar el primer activo</button>
              <button type="button" className="pe2-btn" onClick={() => setPanel({ tipo: 'importar' })}>Importar desde Excel</button>
            </div>
          ) : <p className="pe2-nota">Pide a un administrador que registre los activos.</p>}
        </>
      )}
    </div>
  );

  return (
    <div className="pe2 mant">
      <div>
        <span className="pe2-paso">Mantenimiento</span>
        <h2>Inventario de activos</h2>
        <p className="pe2-sub">Registro único de máquinas, equipos, instalaciones y equipos de emergencia, con su estado operativo, su aptitud de seguridad, documentos, historial y QR.</p>
      </div>
      {error && <div className="pe2-error" role="alert">{error}</div>}
      {aviso && <div className="pe2-aviso" role="status">{aviso} <button type="button" className="mant-cerrar-aviso" onClick={() => setAviso('')} aria-label="Cerrar el aviso">×</button></div>}
      {cargando ? <p className="pe2-nota">Cargando…</p> : (
        <>
          <div className="pe2-acciones mant-acciones">
            <label className="pe2-check"><input type="checkbox" checked={verArchivados} onChange={e => setVerArchivados(e.target.checked)} />Ver archivados</label>
            {filas.length > 0 && <button type="button" className="pe2-btn" onClick={() => exportar(visibles)}>Exportar lo visible ({visibles.length})</button>}
            <button type="button" className="pe2-btn" onClick={() => setPanel({ tipo: 'plazos' })}>Plazos de extintores</button>
            {puedeEditar && <button type="button" className="pe2-btn" onClick={() => setPanel({ tipo: 'importar' })}>Importar</button>}
            {puedeEditar && <button type="button" className="pe2-btn primario" onClick={() => setPanel({ tipo: 'nuevo' })}>Nuevo activo</button>}
          </div>
          <Tablero<FilaActivo>
            board="activos" companyId={companyId} userId={usuarioId} filas={filas} idDe={a => a.id} columnas={columnas} celda={celda}
            filtros={defsFiltro} estadoFiltros={filtros} onFiltros={setFiltros} busqueda={busqueda} onBusqueda={setBusqueda}
            agrupaciones={agrupaciones} grupo={grupo} onGrupo={setGrupo} grupoPorDefecto="estado" tarjetas={tarjetas}
            puedeEditar={puedeEditar && !verArchivados} onEditar={editarCelda} onAbrir={id => setPanel({ tipo: 'ficha', id })}
            abierto={panel?.tipo === 'ficha' ? panel.id : null} accionesMasivas={acciones} vacio={vacio}
            etiquetaFila={a => `${a.code} ${a.name}`} nombreUnidad={['activo', 'activos']} />
        </>
      )}
      {panel && (
        <PanelLateral
          titulo={panel.tipo === 'nuevo' ? 'Nuevo activo' : panel.tipo === 'importar' ? 'Importar activos' : panel.tipo === 'plazos' ? 'Plazos de extintores'
            : abierto ? `${abierto.code} · ${abierto.name}` : 'Activo'}
          subtitulo={abierto ? etiquetaCategoria(abierto.category) : undefined}
          onCerrar={() => setPanel(null)} ancho={panel.tipo === 'importar' ? 760 : 620}>
          {panel.tipo === 'importar' ? (
            <ImportarActivos companyId={companyId} contexto={{ sedes, existentes: activos }} onImportado={recargar} />
          ) : panel.tipo === 'plazos' ? (
            <PlazosExtintor companyId={companyId} plazos={plazos.plazos} propios={plazos.propios} puedeEditar={puedeEditar}
              onGuardado={(p, propios) => setPlazos({ plazos: p, propios })} />
          ) : panel.tipo === 'ficha' && !abierto ? <p className="pe2-nota">El activo ya no está disponible.</p> : (
            <FichaActivo activo={abierto} companyId={companyId} empresa={empresa?.name ?? ''} sedes={sedes} varias={varias} opciones={opciones}
              codigos={codigos} puedeEditar={puedeEditar} esAdmin={esAdmin} usuarioId={usuarioId} hoy={hoy} plazos={plazos.plazos}
              onGuardado={a => { actualizarLocal(a); if (panel.tipo === 'nuevo') setPanel({ tipo: 'ficha', id: a.id }); }}
              onBorrado={id => { setActivos(l => l.filter(x => x.id !== id)); setPanel(null); setAviso('Activo borrado.'); }} />
          )}
        </PanelLateral>
      )}
    </div>
  );
}

function FormMasivo({ etiqueta, opciones, ids, cerrar, aplicar }: { etiqueta: string; opciones: { valor: string; etiqueta: string }[]; ids: string[];
  cerrar: () => void; aplicar: (v: string) => Promise<void> }) {
  const [v, setV] = useState(opciones[0]?.valor ?? '');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  return (
    <>
      <label className="pe2-campo"><span>{etiqueta}</span>
        <select value={v} onChange={e => setV(e.target.value)}>{opciones.map(o => <option key={o.valor} value={o.valor}>{o.etiqueta}</option>)}</select>
      </label>
      <button type="button" className="pe2-btn primario" disabled={ocupado} onClick={async () => {
        setOcupado(true); setError('');
        try { await aplicar(v); cerrar(); } catch (e) { setError(msg(e)); }
        setOcupado(false);
      }}>{ocupado ? 'Aplicando…' : `Aplicar a ${ids.length} ${ids.length === 1 ? 'activo' : 'activos'}`}</button>
      <button type="button" className="pe2-btn" onClick={cerrar}>Cancelar</button>
      {error && <p className="tab-error" role="alert">{error}</p>}
    </>
  );
}

function FormUbicacion({ sedes, ids, cerrar, aplicar }: { sedes: { valor: string; etiqueta: string }[]; ids: string[]; cerrar: () => void;
  aplicar: (sede: string, area: string) => Promise<void> }) {
  const [sede, setSede] = useState(sedes[0]?.valor ?? '');
  const [area, setArea] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  return (
    <>
      {sedes.length > 0 && (
        <label className="pe2-campo"><span>Sede</span>
          <select value={sede} onChange={e => setSede(e.target.value)}>{sedes.map(o => <option key={o.valor} value={o.valor}>{o.etiqueta}</option>)}</select>
        </label>
      )}
      <label className="pe2-campo"><span>Área</span><input type="text" maxLength={160} value={area} onChange={e => setArea(e.target.value)} /></label>
      <button type="button" className="pe2-btn primario" disabled={ocupado} onClick={async () => {
        setOcupado(true); setError('');
        try { await aplicar(sede, area); cerrar(); } catch (e) { setError(msg(e)); }
        setOcupado(false);
      }}>{ocupado ? 'Aplicando…' : `Aplicar a ${ids.length} ${ids.length === 1 ? 'activo' : 'activos'}`}</button>
      <button type="button" className="pe2-btn" onClick={cerrar}>Cancelar</button>
      {error && <p className="tab-error" role="alert">{error}</p>}
    </>
  );
}
